import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Redirect } from 'react-router-dom';
import { Grid } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { withI18n } from 'react-i18next';
import shortid from 'shortid';
import ProductDetail from '@/components/Products/ProductDetail';
import ModalSetPassword from '@/components/Modal/ModalSetPassword';
import FaqProductDetail from '@/components/FrequentlyQuestions/FaqProductDetail';
import {
  loadProductDetail, removeProductDetail, healthcheck, toggleAccessPasswordModal,
  updateOptionsProductDetail,
} from '@/redux/actions/productDetail';
import {
  AVAILABLE_REBOOT_PRODUCT_ID_LIST, AVAILABLE_PRODUCT_TYPES, AVAILABLE_PRODUCT_DOMAIN_STATUS,
  AVAILABLE_DEDICATED_TYPES,
} from '@/config/products/availability';
import VpsCustomizer from '../VpsOciDetailPage/components/VpsCustomizer';
import RebootCard from '@/components/Cards/RebootCard';
import ProductsContextProvider from '@/contexts/Products/context';
import { BottomAdvertisementHandler } from '@/pages/advertisement/BottomAdvertisementHandler';
import styles from './styles';
import { locale } from '@/utils/locale';
import {
  backupActions, domainsActions, preChatActions,
} from '@/redux/modules';
import { isBRServer } from '@/utils/Validators/validation';
import { TempDomainModal } from '../TempDomainModal';
import { HgProducts } from '@/components/SupportForms/SupportForms.types';

import VpsOciDetailPage from '../VpsOciDetailPage';
import { ENABLE_VPS_OCI_DETAIL_PAGE } from '@/config/GrowthBook/constants';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import Loader from '@/components/Layout/Loader';

import SupportForms from '@/components/SupportForms';

const LOCATION_PRODUCTS = 'produtos';

const ProductDetailPage = ({ classes }) => {
  const dispatch = useDispatch();
  const [hasRebootCard, setHasRebootCard] = useState(false);
  const [showFormChangePlanVpsOci, setShowFormChangePlanVpsOci] = useState(false);
  const [isDedicated, setIsDedicated] = useState(false);
  const [temporaryDomainModal, setTemporaryDomainModal] = useState([]);
  const [temporaryDomainModalOption, setTemporaryDomainModalOption] = useState(null);

  const { product, loading: productDetailLoading } = useSelector(state => state.productDetail);
  const passwordChanged = useSelector(state => (state.productDetail.healthcheck ? state.productDetail.healthcheck.passChanged : true));
  const serverStatus = useSelector(state => (state.productDetail.healthcheck ? state.productDetail.healthcheck.status : null));
  const showAccessPasswordModal = useSelector(state => state.productDetail.showAccessPasswordModal);
  const { productsList: productsByStatus } = useSelector(state => state.preChat);
  const isDedicatedType = product && AVAILABLE_DEDICATED_TYPES.includes(product.type);
  const { id: productId } = useParams();
  const { temporaryDomains } = useSelector(state => state.newDomains);

  const isVpsOCIEnabled = useFeatureIsOn(ENABLE_VPS_OCI_DETAIL_PAGE);
  const isVpsOCI = (product && HgProducts.VPS_OCI.includes(product.packageid)) || null;

  const handleShowTempDomainModal = useCallback(({ temporaryDomains, label }) => {
    setTemporaryDomainModal(temporaryDomains);
    setTemporaryDomainModalOption(label);
  }, []);

  const getTemporaryDomains = useCallback(() => {
    if (!temporaryDomains.temporaryDomainsLoaded) {
      dispatch(domainsActions.temporaryDomains.request());
    }
  }, [dispatch, temporaryDomains.temporaryDomainsLoaded]);

  useCallback(() => {
    getTemporaryDomains();
  }, [getTemporaryDomains]);

  const getProductsByStatus = useCallback(() => {
    dispatch(preChatActions.productByStatus.request({ withCancelled: true, withPending: true }));
  }, []);

  useEffect(() => {
    getProductsByStatus();
  }, [getProductsByStatus]);

  const updateOptionsAfterStandaloneActivation = async () => {
    const storageActiveKey = 'id-sitebuilder-standalone-activated';
    const siteBuilderStandaloneId = await localStorage.getItem(storageActiveKey);
    if (siteBuilderStandaloneId && siteBuilderStandaloneId === productId) {
      localStorage.removeItem(storageActiveKey);

      const arrActions = [
        {
          uuid: 'active-edit-site-1',
          relid: shortid.generate(),
          linkUrl: `${locale('routes.siteBuilderRedirectAccess')}/${product.domain}`,
          type: 'websitebuilder-active-edit',
          status: 'active',
        },
        {
          uuid: 'removeSite-1',
          relid: shortid.generate(),
          linkUrl: '_self',
          type: 'websitebuilder',
          status: 'active',
          removeSite: true,
        },
      ];

      dispatch(updateOptionsProductDetail(arrActions));
    }
  };

  useEffect(() => {
    product && window.addEventListener('storage', updateOptionsAfterStandaloneActivation);
    return () => {
      window.removeEventListener('storage', updateOptionsAfterStandaloneActivation);
    };
  }, [product]);

  useEffect(() => {
    dispatch(loadProductDetail(productId));
    dispatch(backupActions.backupLog.request({ id: productId }));
    return () => {
      dispatch(removeProductDetail());
    };
  }, [productId]);

  useEffect(() => {
    if (!passwordChanged && serverStatus === 'active') {
      dispatch(toggleAccessPasswordModal());
    }
  }, [passwordChanged, serverStatus]);

  const checkCardVisibilityById = id => AVAILABLE_REBOOT_PRODUCT_ID_LIST[isBRServer ? 'br' : 'es'].includes(id);

  useEffect(() => {
    if (product && isDedicatedType) {
      const isProductActive = product.domainstatus === 'Active';

      isProductActive && dispatch(healthcheck(productId));

      const rebootCard = (checkCardVisibilityById(product.packageid) && isProductActive);
      setHasRebootCard(rebootCard);
      setIsDedicated(isDedicatedType);
    }
  }, [
    product && product.domainstatus,
    product && product.packageid,
  ]);

  const handleCloseModalPassword = () => {
    dispatch(toggleAccessPasswordModal());
  };

  const handleOpenModalPassword = () => {
    dispatch(toggleAccessPasswordModal());
  };

  const clearTemporayDomainModal = () => {
    setTemporaryDomainModal(null);
    setTemporaryDomainModalOption(null);
  };

  const changePlanVpsOciProps = useCallback(() => {
    const defaultProps = {
      locales: {
        title: locale('vps.oci.changePlan.hireBanner.title'),
        description: locale('vps.oci.changePlan.hireBanner.description'),
        buttonLabel: locale('vps.oci.changePlan.hireBanner.button'),
      },
    };

    const onGoToChangePlanPage = () => {
      setShowFormChangePlanVpsOci(true);
    };

    return {
      ...defaultProps,
      onGoToChangePlanPage,
      showFormChangePlanVpsOci,
      setShowFormChangePlanVpsOci,
    };
  }, [showFormChangePlanVpsOci]);

  if (productDetailLoading || (temporaryDomains && temporaryDomains.temporaryDomainsLoaded)) {
    return <Loader />;
  }

  if (!productDetailLoading
    && !!product
    && (!AVAILABLE_PRODUCT_TYPES.includes(product.type) || !AVAILABLE_PRODUCT_DOMAIN_STATUS.includes(product.domainstatus))) {
    return <Redirect to={`${locale('routes.products')}`} />;
  }
  const checkStatusActive = product && product.domainstatus === 'Active';
  const showChangePlanVpsOci = (product && HgProducts && HgProducts.VPS_OCI) && HgProducts.VPS_OCI.includes(product.packageid) && checkStatusActive;

  if (showFormChangePlanVpsOci) {
    const productName = product && product.productname;
    const domain = product && product.domain;
    const getProductNamePackageVPSOci = (productName && domain) ? `${productName} - ${domain}` : '';

    return (
      <SupportForms
        serviceType={{
          title: locale('vps.oci.changePlan.form.title'),
          onSuccess: () => setShowFormChangePlanVpsOci(false),
          onBackForm: () => setShowFormChangePlanVpsOci(false),
          lockedOptions: getProductNamePackageVPSOci !== '',
          defaultValues: {
            defaultSelectedProductOption: getProductNamePackageVPSOci,
            defaultSelectedSector: 'billing',
            defaultSelectedNeed: locale('vps.oci.changePlan.form.defaultSelectedNeed'),
          },
        }}
      />
    );
  }

  // Deixar inativo
  if (isVpsOCI && isVpsOCIEnabled) {
    return <VpsOciDetailPage />;
  }

  return (
    <>
      {temporaryDomainModal && !!temporaryDomainModal.length && (
      <TempDomainModal
        temporaryDomainModal={temporaryDomainModal}
        setTemporaryDomainModal={setTemporaryDomainModal}
        onClose={clearTemporayDomainModal}
        optionLabel={temporaryDomainModalOption}
      />
      )}

      {product && (
        <ProductsContextProvider>
          <div className={classes.contentDetails}>
            <Grid container spacing={16}>
              <Grid item xs={12}>
                <ProductDetail
                  product={product}
                  isDedicated={isDedicated}
                  disabledActions={isDedicated ? !passwordChanged : false}
                  handleShowTempDomainModal={handleShowTempDomainModal}
                  temporaryDomains={temporaryDomains}
                  productsByStatus={productsByStatus}
                />
              </Grid>
            </Grid>
            <Grid container className={classes.contentSpacing} spacing={32}>

              {hasRebootCard && (
                <Grid item xs={12} sm={12} md={6} xl={6} className={classes.wrapperRight}>
                  <RebootCard disabledActions={!passwordChanged} />
                </Grid>
              )}

              {showChangePlanVpsOci && (
                <Grid item xs={12} sm={12} md={6} xl={6} className={classes.wrapperRight}>
                  <VpsCustomizer
                    {...changePlanVpsOciProps()}
                  />
                </Grid>
              )}
            </Grid>
          </div>
          <ModalSetPassword
            open={showAccessPasswordModal}
            onOpen={handleOpenModalPassword}
            onClose={handleCloseModalPassword}
            editing={passwordChanged}
            product={product.productname}
          />
        </ProductsContextProvider>
      )}

      <div className={classes.bottomInfoWrapper} data-testid="bottom-faq-products">
        <FaqProductDetail packageid={product && product.packageid} />
        <BottomAdvertisementHandler advertisementLocal={LOCATION_PRODUCTS} />
      </div>
    </>
  );
};

export default withI18n()(withStyles(styles)(ProductDetailPage));
